import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  GraduationCap, 
  ShieldCheck, 
  ChevronLeft, 
  Sparkles
} from 'lucide-react';
import Alert from '../../components/common/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForceLoginButton, setShowForceLoginButton] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const toast = useToast();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // منطق معالجة الدخول (كامل وبدون أي اختصار)
  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);
    setShowForceLoginButton(false);
    
    // Add remember me to login data
    const loginData = {
      ...data,
      remember: rememberMe
    };
    
    try {
      const result = await login(loginData);
      
      if (result.success) {
        toast.success('مرحباً بك مجدداً في منصتك التعليمية');
      }
    } catch (err) {
      // Login error handling
      
      // Handle active session error (409)
      if (err.response?.status === 409 && err.response?.data?.code === 'ACTIVE_SESSION_EXISTS') {
        const sessionInfo = err.response.data.sessionInfo;
        
        setError(
          `يوجد جلسة نشطة من جهاز آخر.
          
تم تسجيل الدخول في: ${new Date(sessionInfo.createdAt).toLocaleString('ar-EG')}
من عنوان IP: ${sessionInfo.ipAddress}

هل تريد تسجيل الخروج من الجهاز الآخر؟`
        );
        
        // Show force login button
        setShowForceLoginButton(true);
        
      } else {
        // Other errors
        const errorMessage = err.response?.data?.error || 'فشل تسجيل الدخول';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Force login handler (optional feature)
  const handleForceLogin = async () => {
    setIsLoading(true);
    
    try {
      // Get form data
      const form = document.querySelector('form');
      const formData = new FormData(form);
      const data = {
        email: formData.get('email'),
        password: formData.get('password')
      };
      
      // Call force login endpoint (if exists) or regular login
      const result = await login(data);
      
      if (result.success) {
        toast.success('تم تسجيل الدخول وإنهاء الجلسة الأخرى');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'فشل تسجيل الدخول';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      
      {/* Decorative Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10"></div>

      <div className="max-w-md mx-auto w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        {/* Branding Header */}
        <div className="text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="bg-primary-600 p-4 rounded-[1.5rem] shadow-xl shadow-primary-200 transform group-hover:rotate-12 transition-all duration-500">
                <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div className="text-right">
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">مـنـصـتـي</h1>
                <p className="text-primary-600 text-xs font-black uppercase tracking-[0.2em] mt-1">بوابتك نحو التفوق</p>
            </div>
          </Link>
          <div className="space-y-1">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">تسجيل الدخول</h2>
            <p className="text-gray-500 font-medium">استكمل رحلتك التعليمية اليوم</p>
          </div>
        </div>

        {/* Form Main Card */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 p-8 lg:p-10 border border-gray-100">
          
          {error && (
            <div className="mb-6">
                <Alert 
                    type="error" 
                    message={error} 
                    dismissible 
                    onDismiss={() => setError(null)} 
                />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                البريد الإلكتروني
              </label>
              <div className="relative group">
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'تنسيق البريد الإلكتروني غير صحيح',
                    },
                  })}
                  type="email"
                  placeholder="example@email.com"
                  autocomplete="email"
                  className={`
                    w-full pr-12 pl-4 py-4 bg-gray-50 border rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold
                    ${errors.email ? 'border-red-300' : 'border-gray-100 focus:border-primary-500'}
                  `}
                />
              </div>
              {errors.email && (
                <p className="mt-1 mr-2 text-[10px] text-red-500 font-black flex items-center gap-1"><Sparkles size={10}/> {errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex justify-between items-center px-2">
                <label htmlFor="password" className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    كلمة المرور
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[10px] font-black text-primary-600 hover:text-primary-800 transition-colors"
                >
                    نسيت كلمة السر؟
                </Link>
              </div>
              <div className="relative group">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  {...register('password', {
                    required: 'كلمة المرور مطلوبة',
                    minLength: { value: 6, message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autocomplete="current-password"
                  className={`
                    w-full pr-12 pl-12 py-4 bg-gray-50 border rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold
                    ${errors.password ? 'border-red-300' : 'border-gray-100 focus:border-primary-500'}
                  `}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 mr-2 text-[10px] text-red-500 font-black flex items-center gap-1"><Sparkles size={10}/> {errors.password.message}</p>
              )}
            </div>

            {/* Remember Me Toggle */}
            <div className="flex items-center px-2">
              <label className="relative inline-flex items-center cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only peer" 
                />
                <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                <span className="mr-3 text-xs font-bold text-gray-500 group-hover:text-gray-700">تذكر تسجيل دخولي</span>
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-5 bg-primary-600 text-white rounded-[2rem] font-black text-lg shadow-xl shadow-primary-200 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                    {isLoading ? <LoadingSpinner size="small" color="white" /> : <ShieldCheck size={24} />}
                    تسجيل الدخول للـمـنـصـة
                </button>
            </div>

            {/* Force Login Button (shown when active session exists) */}
            {showForceLoginButton && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleForceLogin}
                  disabled={isLoading}
                  className="w-full py-4 bg-red-600 text-white rounded-[2rem] font-bold text-sm shadow-lg shadow-red-200 hover:bg-red-700 transition-all transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? <LoadingSpinner size="small" color="white" /> : <ShieldCheck size={20} />}
                  تسجيل الدخول وإنهاء الجلسة الأخرى
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  سيتم تسجيل الخروج تلقائياً من الجهاز الآخر
                </p>
              </div>
            )}
          </form>

          {/* Registration Link */}
          <div className="mt-10 text-center">
            <p className="text-gray-500 font-bold text-sm">
              ليس لديك حساب تعليمي بعد؟
              <Link to="/register" className="text-primary-600 font-black flex items-center justify-center gap-1 mt-2 hover:gap-3 transition-all">
                أنشئ حسابك الآن مجاناً <ChevronLeft size={18} />
              </Link>
            </p>
          </div>
        </div>

        {/* Bottom Legal Info */}
        <p className="text-center text-[10px] text-gray-400 font-medium px-4 leading-relaxed tracking-wider uppercase">
          بالدخول إلى حسابك، أنت توافق تماماً على <button type="button" onClick={() => navigate('/terms')} className="text-primary-600 font-black border-b border-primary-200 bg-none cursor-pointer hover:text-primary-700">شروط الاستخدام</button> و <button type="button" onClick={() => navigate('/privacy')} className="text-primary-600 font-black border-b border-primary-200 bg-none cursor-pointer hover:text-primary-700">سياسة الخصوصية</button> الخاصة بمنصة منصتي
        </p>
      </div>
    </div>
  );
};

export default Login;